import React, { useCallback, useMemo, useEffect } from 'react';
import { ResponseErrorPanel, TableColumn } from '@backstage/core-components';
import { CopyableText } from '../../CopyableText';
import { ModernResourceView } from '../../ModernResourceView';
import {
  AwsActionMenu,
  AwsActionConfirmationDialog,
  RdsConnectionDialog,
} from '../actions';
import { RdsTableData } from './types';
import { getTeleportDocsUrl, mapResourcesToRdsTableData } from './utils';
import { ExecuteActionOptions } from '../../../api';
import {
  RdsResourceDetails,
  CRDAWSConfig,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import {
  configApiRef,
  useApi,
  errorApiRef,
  alertApiRef,
} from '@backstage/core-plugin-api';
import { useActionManagement } from './useActionManagement';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';
import { useAwsContext } from '../AwsContext';

interface RdsTableProps {
  entityRef: string;
  awsConfig: CRDAWSConfig;
  title: string;
}

export const RdsTable: React.FC<RdsTableProps> = ({
  entityRef,
  awsConfig,
  title,
}) => {
  const { api, projectCode, refreshContext } = useAwsContext();

  const executeAction = useCallback(
    (options: ExecuteActionOptions) => {
      return api.executeAction(options);
    },
    [api],
  );

  const {
    value: resources,
    loading: isLoading,
    error: error,
    retry: refresh,
  } = useAsyncRetry(async () => {
    if (!projectCode) return [];
    return api.listResources({
      serviceType: 'aws',
      resourceType: 'rds',
      projectCode: projectCode,
      entityRef: entityRef,
      cloudConfig: awsConfig,
    });
  }, [projectCode, api]);

  const handleRefresh = () => {
    // Invalidate cache for this specific AWS RDS configuration
    api.invalidateCache('listResources');
    refresh();
  };

  const config = useApi(configApiRef);
  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);

  const {
    menuPosition,
    confirmationDialogOpen,
    rdsConnectionDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    handleOpenConnection,
    setConfirmationDialogOpen,
    setRdsConnectionDialogOpen,
  } = useActionManagement({
    executeAction,
    refresh,
    errorApi,
    alertApi,
    awsConfig,
    entityRef,
    projectCode,
  });

  useEffect(() => {
    if (!refreshContext) return;

    const refreshKey = `aws:rds:${awsConfig.accountId}:${awsConfig.region}`;
    const unregister = refreshContext.registerRefresh(refreshKey, refresh);

    return unregister;
  }, [refreshContext, refresh, awsConfig.accountId, awsConfig.region]);

  const columns = useMemo<TableColumn<RdsTableData>[]>(
    () => [
      {
        title: 'ID',
        field: 'identifier',
        render: (row: RdsTableData) => (
          <div style={{ maxWidth: '400px' }}>
            <CopyableText value={row.identifier} />
          </div>
        ),
      },
      {
        title: 'Name',
        field: 'name',
      },
      {
        title: 'Engine',
        field: 'engine',
      },
      {
        title: 'Class',
        field: 'class',
      },
      {
        title: 'Status',
        field: 'status',
      },
    ],
    [],
  );

  const data = useMemo(
    () =>
      resources
        ? mapResourcesToRdsTableData(resources as RdsResourceDetails[])
        : [],
    [resources],
  );

  if (error) {
    error.message = `Failed to load AWS RDSs`;
    return <ResponseErrorPanel error={error} title={error.message} />;
  }

  return (
    <>
      <ModernResourceView<RdsTableData>
        title={title}
        data={data}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onActions={handleTableActionClick}
        cloudService="aws"
        resourceType="rds"
        columns={columns}
        colorTheme="#FF9900" // AWS Orange
        emptyMessage="No RDS instances found"
      />

      <AwsActionMenu
        handleActionClick={handleActionClick}
        handleMenuClose={handleMenuClose}
        handleOpenConnection={handleOpenConnection}
        menuPosition={menuPosition}
        service="rds"
      />

      <AwsActionConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmAction}
        action={selectedAction as any}
        isLoading={isActionInProgress}
        service="rds"
      />

      <RdsConnectionDialog
        open={rdsConnectionDialogOpen}
        onClose={() => setRdsConnectionDialogOpen(false)}
        teleportUrl={getTeleportDocsUrl(config)}
      />
    </>
  );
};
